/**
 * PayrollApprovalTimeline — compact progress indicator for a PayrollPeriod's lifecycle.
 * DRAFT → IMPORTED → CALCULATED → PENDING_APPROVAL → APPROVED → PAID, with a REJECTED branch
 * that reopen() sends back to CALCULATED. Shows only the period's actual current stage (plus a
 * slim progress bar) rather than enumerating all five stages, so the page stays scannable.
 */
import type { PayrollPeriod } from "../types";
import { fmtDateTime, PeriodStatusBadge } from "./PayrollUI";

const STAGES: { label: string; statusKey: string; date: (p: PayrollPeriod) => string | null | undefined }[] = [
  { label: "Imported",   statusKey: "IMPORTED",          date: () => undefined },
  { label: "Calculated", statusKey: "CALCULATED",        date: (p) => p.calculatedAt },
  { label: "Submitted",  statusKey: "PENDING_APPROVAL",  date: (p) => p.submittedAt },
  { label: "Approved",   statusKey: "APPROVED",          date: (p) => p.approvedAt },
  { label: "Paid",       statusKey: "PAID",              date: (p) => p.paidAt },
];

export function PayrollApprovalTimeline({ period }: { period: PayrollPeriod }) {
  const isRejected = period.status === "REJECTED";
  const stageIdx = STAGES.findIndex((s) => s.statusKey === (isRejected ? "PENDING_APPROVAL" : period.status));
  const current = stageIdx >= 0 ? STAGES[stageIdx] : null;
  const pct = stageIdx >= 0 ? Math.round(((stageIdx + 1) / STAGES.length) * 100) : 0;
  const currentDate = current?.date(period);

  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-bold text-slate-900">Progress</h2>
        <PeriodStatusBadge value={period.status} />
      </div>

      {isRejected && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          This period was rejected{period.rejectionReason ? `: ${period.rejectionReason}` : "."}
          <span className="block mt-1 text-xs">Use Reopen to send it back to Calculated for correction.</span>
        </div>
      )}

      {current ? (
        <>
          <div className="flex items-center justify-between text-sm">
            <span className={`font-semibold ${isRejected ? "text-red-700" : "text-emerald-800"}`}>{current.label}</span>
            {currentDate && <span className="text-xs text-slate-400">{fmtDateTime(currentDate)}</span>}
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full transition-all ${isRejected ? "bg-red-400" : "bg-emerald-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Stage {stageIdx + 1} of {STAGES.length}</p>
        </>
      ) : (
        <p className="text-sm text-slate-500">Draft — basic pay not yet imported.</p>
      )}

      {period.currentApproverName && period.status === "PENDING_APPROVAL" && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Awaiting approval from <strong>{period.currentStepName ?? "the approver"}</strong> — {period.currentApproverName}
        </div>
      )}
    </div>
  );
}
